import { useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Button,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useAuth } from '../../auth/useAuth';
import {
  portalColors,
  portalGradients,
  portalSpacing,
  portalTypography,
} from '../../styles/theme';
import { NavMenu } from './NavMenu';

const useStyles = makeStyles({
  nav: {
    display: 'flex',
    alignItems: 'center',
    backgroundImage: portalGradients.navBackground,
    borderBottomWidth: tokens.strokeWidthThin,
    borderBottomStyle: 'solid',
    borderBottomColor: portalColors.navBorder,
    paddingLeft: portalSpacing.xxxl,
    paddingRight: portalSpacing.xxxl,
    height: portalSpacing.navHeight,
    position: 'sticky',
    top: '0',
    zIndex: 100,
    columnGap: portalSpacing.m,
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  logoLink: {
    textDecorationLine: 'none',
    color: portalColors.bodyText,
    display: 'flex',
    alignItems: 'center',
    gap: portalSpacing.sNudge,
    ':hover': {
      textDecorationLine: 'none',
    },
  },
  logoImg: {
    height: '28px',
    width: 'auto',
  },
  docsLabel: {
    color: portalColors.mutedText,
    fontSize: portalTypography.docs,
    fontWeight: portalTypography.weightSemibold,
    fontFamily: portalTypography.fontFamilyBase,
    lineHeight: '1.2',
  },
  // Auth actions are pinned (never overflow). They are session actions, not
  // navigation, so they stay anchored to the right at every width while the
  // nav links collapse into the overflow menu.
  right: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: portalSpacing.m,
  },
  profileLink: {
    textDecorationLine: 'none',
    color: portalColors.bodyText,
    fontSize: portalTypography.navLink,
    fontWeight: portalTypography.weightRegular,
    fontFamily: portalTypography.fontFamilyBase,
    ':hover': {
      color: portalColors.brand,
      textDecorationLine: 'none',
    },
  },
  signOutButton: {
    backgroundColor: portalColors.brand,
    color: portalColors.signOutText,
    borderTopWidth: '0',
    borderRightWidth: '0',
    borderBottomWidth: '0',
    borderLeftWidth: '0',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: portalTypography.navLink,
    fontWeight: portalTypography.weightRegular,
    fontFamily: portalTypography.fontFamilyBase,
    paddingTop: portalSpacing.xs,
    paddingBottom: portalSpacing.xs,
    paddingLeft: portalSpacing.l,
    paddingRight: portalSpacing.l,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: portalColors.brandHover,
    },
  },
});

/**
 * Top-level navigation shell. Composes layout (logo + auth actions) with the
 * width-aware `NavMenu`. The nav links collapse into `NavMenu`'s overflow menu
 * based on the actual space between the logo and the auth actions, so there is
 * no separate mobile-menu surface or breakpoint to keep in sync.
 */
export function Navigation(): JSX.Element {
  const styles = useStyles();
  const location = useLocation();
  const { isAuthenticated, isOps, loginRedirect, logout, signup } = useAuth();

  const handleLogin = useCallback(async () => {
    await loginRedirect();
  }, [loginRedirect]);

  const handleSignup = useCallback(async () => {
    await signup();
  }, [signup]);

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  return (
    <nav className={styles.nav}>
      <div className={styles.left}>
        <Link to="/" className={styles.logoLink}>
          <img
            src="/images/foxit-logo-orange.svg"
            alt="Foxit"
            className={styles.logoImg}
          />
          <span className={styles.docsLabel}>DOCS</span>
        </Link>
      </div>

      {/* Center: Nav links */}
      <NavMenu
        currentPath={location.pathname}
        isAuthenticated={isAuthenticated}
        isOps={isOps}
      />

      <div className={styles.right}>
        {isAuthenticated ? (
          <>
            <Link to="/profile" className={styles.profileLink}>
              Profile
            </Link>
            <button className={styles.signOutButton} onClick={handleLogout}>
              Sign Out
            </button>
          </>
        ) : (
          <>
            <Button appearance="subtle" onClick={handleLogin}>
              Sign In
            </Button>
            <Button appearance="primary" onClick={handleSignup}>
              Sign Up
            </Button>
          </>
        )}
      </div>
    </nav>
  );
}
